// Slice 2C3 · Canonical family-setup metadata + status helpers.
// Kept small so the wizard, standalone pages, and tests all agree on
// the canonical family order and copy without duplicating strings.

export const FAMILY_ORDER = [
  "proposal",
  "contract",
  "invoice",
  "customer_update",
  "closeout",
  "warranty",
];

export const FAMILY_META = {
  proposal: {
    key: "proposal",
    title: "Proposal defaults",
    purpose: "Standard wording for future proposals and payment terms.",
  },
  contract: {
    key: "contract",
    title: "Contract defaults",
    purpose:
      "Standard contract terms, disclaimers, and customer approval language.",
  },
  invoice: {
    key: "invoice",
    title: "Invoice defaults",
    purpose: "Standard invoice notes and payment instructions.",
  },
  customer_update: {
    key: "customer_update",
    title: "Customer update defaults",
    purpose:
      "Standard signature, review, and referral wording for contractor-approved updates.",
  },
  closeout: {
    key: "closeout",
    title: "Closeout defaults",
    purpose:
      "Standard maintenance, closeout, review, and referral wording for future closeout packets.",
  },
  warranty: {
    key: "warranty",
    title: "Warranty defaults",
    purpose:
      "Standard workmanship warranty terms, manufacturer warranty handling, and warranty-service contact wording.",
  },
};

export const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  ready: "Ready",
  later: "Set up later",
};

export const READY_HELPER_TEXT =
  "Ready means you reviewed this section and accept your saved wording or JobClose AI’s built-in defaults. You can still edit future documents before sending.";

// Given a derived-status map, return the first family (in canonical order)
// whose status is NOT one of ready/later. Returns null when all resolved.
export const firstUnresolvedFamily = (derived) => {
  if (!derived) return null;
  for (const fam of FAMILY_ORDER) {
    const s = derived[fam];
    if (s !== "ready" && s !== "later") return fam;
  }
  return null;
};

// Resolve a possibly-invalid `family` query parameter against the current
// derived-status map. Falls back to the first unresolved family, or to
// Proposal when every family is already resolved.
export const resolveFamilyParam = (queryFamily, derived) => {
  if (queryFamily && FAMILY_ORDER.includes(queryFamily)) return queryFamily;
  return firstUnresolvedFamily(derived) || "proposal";
};

// Contextual action label per current status.
export const contextualActionLabel = (status) => {
  switch (status) {
    case "not_started": return "Set up";
    case "in_progress": return "Continue setup";
    case "ready":       return "Edit setup";
    case "later":       return "Set up now";
    default:            return "Set up";
  }
};

// Step 3 is complete only when every family is ready or later AND no
// family carries a blocking placeholder / example / fake-contact
// validation issue (Slice 2C4). ``familyValidation`` is optional so
// callers that do not care about placeholder blocking (Slice 2C3
// baseline behaviour) can still call this as before.
export const isStep3Complete = (derived, familyValidation) => {
  if (!derived) return false;
  if (!FAMILY_ORDER.every((f) => derived[f] === "ready" || derived[f] === "later")) {
    return false;
  }
  if (familyValidation && typeof familyValidation === "object") {
    for (const f of FAMILY_ORDER) {
      if (familyValidation[f]) return false;
    }
  }
  return true;
};
